<template>
  <div>
    <div class="container-fluid">
      <div class="page-header">
        <Breadcrumbs main="eprolo" title="EPROLO Import" />
      </div>
      <b-modal id="EproloAlert" title="Admin" ok-only>
        <p class="my-4">{{ message }}</p>
      </b-modal>

      <div class="row" v-if="loadingStore">
        <div class="col-md-12">
          <div class="card p-4">Loading store settings...</div>
        </div>
      </div>

      <!-- EPROLO not enabled / key missing -->
      <div class="row" v-if="!loadingStore && !configured">
        <div class="col-md-12">
          <div class="card p-4">
            <h5 class="mb-3">EPROLO is not configured for this store</h5>
            <p class="mb-3">
              Enable EPROLO and save the API key and secret in the store
              settings before importing products.
            </p>
            <router-link to="/stores/eprolo" class="btn btn-primary" style="width: 230px;">
              Open EPROLO settings
            </router-link>
          </div>
        </div>
      </div>

      <template v-if="!loadingStore && configured">
        <!-- Toolbar -->
        <div class="row">
          <div class="col-md-12">
            <div class="card p-3">
              <!-- Filtres appliqués par EPROLO avant le chargement : catégorie
                   racine (wareTypeId), sous-catégorie (wareTypeTwoId) et id de
                   produit — vérifiés en direct contre l'API. -->
              <div class="row">
                <div class="col-md-3">
                  <label>Category (EPROLO)</label>
                  <select class="form-control" v-model="typeId" @change="onCategoryChange"
                    :disabled="loadingPage || loadingAll || importing">
                    <option value="">All categories</option>
                    <option v-for="t in types" :key="t.id" :value="String(t.id)">{{ t.name }}</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label>Subcategory (EPROLO)</label>
                  <select class="form-control" v-model="subTypeId"
                    :disabled="loadingPage || loadingAll || importing || loadingSubs || !typeId">
                    <option value="">{{ loadingSubs ? "Loading subcategories..." : (typeId ? "All subcategories" : "Choose a category first") }}</option>
                    <option v-for="s in subTypes" :key="s.id" :value="String(s.id)">{{ s.name }}</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label>EPROLO id</label>
                  <input class="form-control" type="text" v-model="productId" placeholder="Single product id" />
                </div>
                <div class="col-md-1">
                  <label>Page</label>
                  <input class="form-control" type="number" min="1" v-model.number="page"
                    :disabled="loadingPage || loadingAll || importing || !!productId" />
                </div>
                <div class="col-md-3 d-flex align-items-end flex-wrap">
                  <button type="button" class="btn btn-primary mr-2 mb-1" @click="loadPage"
                    :disabled="loadingPage || loadingAll || importing">
                    {{ loadingPage ? "Loading..." : "Load catalog" }}
                  </button>
                  <button type="button" class="btn btn-primary mr-2 mb-1" @click="loadAllPages"
                    :disabled="loadingPage || loadingAll || importing || !!productId">
                    {{ loadingAll ? "Loading all pages..." : "Load all pages" }}
                  </button>
                  <button type="button" class="btn btn-outline-secondary mb-1" @click="resetFilters"
                    :disabled="loadingPage || loadingAll || importing">
                    Reset filters
                  </button>
                </div>
              </div>
              <div class="row mt-3">
                <div class="col-md-3">
                  <label>Search (loaded page)</label>
                  <input class="form-control" type="text" v-model="search" placeholder="Name, SKU..." />
                </div>
                <div class="col-md-2">
                  <label>Margin (%)</label>
                  <input class="form-control" type="number" min="0" v-model.number="margin" />
                </div>
                <div class="col-md-2">
                  <label>EU shipping</label>
                  <input class="form-control" type="number" min="0" step="0.01" v-model.number="shippingEU" />
                </div>
                <div class="col-md-3">
                  <label>Target category (import)</label>
                  <select class="form-control" v-model="targetCategory" :disabled="importing">
                    <option value="">Auto (EPROLO category)</option>
                    <option v-for="c in storeCategories" :key="c.category" :value="c.category">{{ c.category }}</option>
                  </select>
                </div>
                <div class="col-md-2 d-flex align-items-end">
                  <b-form-checkbox v-model="overwrite" class="mb-2">
                    Overwrite existing products
                  </b-form-checkbox>
                </div>
              </div>
              <div class="row mt-2">
                <div class="col-md-12 d-flex justify-content-end">
                  <button type="button" class="btn btn-primary" @click="startImport"
                    :disabled="!selectedCount || importing || loadingAll || loadingPage">
                    {{ importing ? "Importing..." : "Import selected (" + selectedCount + ")" }}
                  </button>
                </div>
              </div>
              <p class="text-muted mt-3 mb-0">
                Store price = (product cost + EU shipping) × (1 + margin / 100) — the worst-case
                shipping is baked into every price, so the Shipping menu of the admin can stay on
                Free shipping. Set the EU shipping value on the EPROLO settings page. Prices are
                rounded up to the next 9.99 and each product gets a compare-at price at least 20%
                above (e.g. sell 29.99, crossed-out 39.99).
              </p>
              <p class="text-muted mt-2 mb-0">
                Each imported product is first pushed to your EPROLO account (EPROLO requires it
                before an order can be placed), then written to this store. All the variants
                (colors, sizes...) are imported with their EPROLO ids: the storefront lets
                customers pick one and the order is sent to EPROLO with the matching variant.
              </p>
              <p class="text-muted mt-2 mb-0">
                Target category: every imported product is filed under the chosen store category
                (create it first on the Categories page); on Auto, each product keeps its EPROLO
                category.
              </p>
              <p class="text-muted mt-2 mb-0">
                Category, subcategory and EPROLO id are applied by EPROLO before the page is
                loaded; the Search box only filters the products already loaded. « Load all
                pages » follows the EPROLO pagination (100 products per page) until the end of
                the selection and replaces the table content — press Stop to interrupt on very
                large categories, then tick the products you want and import them with
                « Import selected ».
              </p>
            </div>
          </div>
        </div>

        <!-- Progress -->
        <div class="row" v-if="importing || importReport || loadingAll">
          <div class="col-md-12">
            <div class="card p-3">
              <div v-if="loadingAll">
                <p class="mb-2">Loading all pages — page {{ loadAllPage }}, {{ rows.length }} products loaded...</p>
                <div class="progress">
                  <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar"
                    style="width: 100%;"></div>
                </div>
                <button type="button" class="btn btn-outline-danger btn-sm mt-2" @click="stopLoadAll">
                  Stop loading
                </button>
              </div>
              <div v-if="importing">
                <p class="mb-2">{{ importLabel }} ({{ importIndex }}/{{ importTotal }})</p>
                <b-progress :value="importIndex" :max="importTotal" variant="primary" animated></b-progress>
              </div>
              <div v-if="importReport" class="mt-2">
                <strong>Import finished:</strong>
                {{ importReport.created }} imported,
                {{ importReport.skipped }} skipped,
                {{ importReport.errors }} error(s).
              </div>
            </div>
          </div>
        </div>

        <!-- Catalog table -->
        <div class="row">
          <div class="col-md-12">
            <div class="card p-3">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <span class="text-muted" v-if="catalogLoaded">
                  {{ loadedPagesLabel || ('Page ' + page) }} — {{ rows.length }} products loaded ({{ filteredRows.length }} shown)<span
                    v-if="loadedFilterLabel"> — EPROLO filter: {{ loadedFilterLabel }}</span>
                </span>
                <span class="text-muted" v-else>Load the EPROLO catalog to see products.</span>
                <div v-if="rows.length">
                  <button type="button" class="btn btn-outline-primary btn-sm mr-2" @click="selectAll"
                    :disabled="importing || loadingAll || loadingPage">
                    Select all shown
                  </button>
                  <button type="button" class="btn btn-outline-secondary btn-sm" @click="clearSelection"
                    :disabled="importing || loadingAll || loadingPage">
                    Clear selection
                  </button>
                </div>
              </div>
              <div class="table-responsive" v-if="filteredRows.length">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th style="width: 40px;"></th>
                      <th style="width: 64px;">Image</th>
                      <th>EPROLO id</th>
                      <th>Name</th>
                      <th>Cost</th>
                      <th>Store price</th>
                      <th>Stock</th>
                      <th>Variants</th>
                      <th>Category</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in filteredRows" :key="row.id">
                      <td>
                        <b-form-checkbox v-model="row._selected" :disabled="importing || loadingAll || loadingPage"></b-form-checkbox>
                      </td>
                      <td>
                        <img v-if="row.imageUrls && row.imageUrls.length" :src="row.imageUrls[0]"
                          class="eprolo-thumb" :title="row.imageUrls.length + ' image(s)'" loading="lazy" />
                        <span v-else class="text-muted">—</span>
                      </td>
                      <td>{{ row.id }}</td>
                      <td>{{ row.name }}</td>
                      <td>{{ row.cost != null ? row.cost.toFixed(2) : "—" }}</td>
                      <td>{{ storePrice(row) }}</td>
                      <td>{{ row.stock != null ? row.stock : "—" }}</td>
                      <td>{{ row.variants }}</td>
                      <td>{{ row.categoryName }}</td>
                      <td>
                        <span v-if="row._status === 'working'" class="badge badge-info">Importing...</span>
                        <span v-else-if="row._status === 'done'" class="badge badge-success">Imported</span>
                        <span v-else-if="row._status === 'skipped'" class="badge badge-secondary">Skipped (exists)</span>
                        <span v-else-if="row._status === 'error'" class="badge badge-danger" :title="row._message">
                          Error
                        </span>
                        <span v-else-if="row._exists" class="badge badge-warning"
                          title="A product with this id already exists in the store">
                          Already in store
                        </span>
                        <span v-else class="badge badge-light">—</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p class="text-muted mb-0" v-else-if="catalogLoaded && rows.length">
                No product on this page matches your search.
              </p>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import firebase from '@/utils/firestore-shim';
import axios from 'axios';
import Cookies from 'js-cookie';
import slug from "slug";
import { apiUrl, uploadHeaders } from '../../utils/imageUpload.js';

const DEFAULT_MARGIN = 30;
const DEFAULT_STOCK = 100;
// Pas de limite de débit documentée chez EPROLO : un petit délai entre les
// produits suffit à rester correct avec l'API.
const IMPORT_INTERVAL = 500;
// Taille de page demandée au catalogue EPROLO : l'API sert 20 produits par
// défaut mais honore le paramètre page_size (plafonné à 200, vérifié en
// direct sur plusieurs catégories et sous-catégories). 100 par page divise
// par 5 le nombre d'appels pendant « Load all pages » tout en restant loin du
// délai d'attente. Au-delà de la dernière page, l'API répond « code -1 : No
// data » (502 du proxy) : c'est la fin naturelle du catalogue, pas un
// incident (cas rencontré quand le nombre total est un multiple exact de la
// taille de page).
const EPROLO_PAGE_SIZE = 100;
// Délai entre deux pages pendant « Load all pages » (lecture seule).
const LOAD_ALL_INTERVAL = 250;
// Garde-fou du chargement complet : arrêt au-delà de 500 pages (~50 000
// produits avec page_size=100) pour ne jamais boucler indéfiniment.
const LOAD_ALL_MAX_PAGES = 500;

function toNumber(value) {
  if (typeof value === 'number' && isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && isFinite(Number(value))) return Number(value);
  return undefined;
}

function round2(value) {
  return Math.round(value * 100) / 100;
}

// Prix « charm » : arrondi AU SUPÉRIEUR au prochain x9.99 (9.99, 19.99,
// 29.99...), minimum 9.99 — jamais en dessous du coût + marge calculé.
function charmPrice(value) {
  var v = Number(value);
  if (!isFinite(v) || v <= 0) return 9.99;
  return round2(Math.ceil((v + 0.01) / 10) * 10 - 0.01);
}

// Firestore document ids cannot contain "/" and a few other characters.
function sanitizeDocId(value) {
  return String(value === undefined || value === null ? '' : value)
    .trim()
    .replace(/[\/\\#?\[\]*\s]+/g, '-');
}

// Identifiants de catégorie EPROLO : wareTypeId est une chaîne « ,24, »
// (plusieurs catégories possibles) — le premier identifiant est retenu.
function firstTypeId(product) {
  var raw = String((product && product.wareTypeId) || '');
  var ids = raw.split(',').map(function (v) { return v.trim(); }).filter(Boolean);
  return ids.length ? ids[0] : '';
}

// Tri d'une liste EPROLO par position croissante : la position la plus basse
// est la variante par défaut du compte (affichée en premier).
function sortByPosition(list) {
  return (Array.isArray(list) ? list : []).slice().sort(function (a, b) {
    return (toNumber(a && a.position) || 0) - (toNumber(b && b.position) || 0);
  });
}

// Groupes de variantes affichés par le storefront (structure « variants » de
// la fiche produit) : noms depuis optionlist (« Color », « Size »...),
// valeurs distinctes de option1/2/3 dans l'ordre des variantes. Deux groupes
// au maximum (sélecteurs variant1/variant2 du template produit) et seules les
// dimensions à plusieurs valeurs sont conservées ; « source » garde le numéro
// d'option d'origine (1..3) pour reconstruire les combinaisons, il n'est pas
// enregistré dans le produit.
function eproloVariantGroups(product) {
  var sorted = sortByPosition(product && product.variantlist);
  if (!sorted.length) return [];
  var names = [];
  if (Array.isArray(product.optionlist)) {
    sortByPosition(product.optionlist).forEach(function (option) {
      if (option && option.name) names.push(String(option.name));
    });
  }
  var groups = [];
  for (var d = 1; d <= 3 && groups.length < 2; d++) {
    var key = 'option' + d;
    var values = [];
    sorted.forEach(function (v) {
      var value = v && v[key] != null ? String(v[key]).trim() : '';
      if (value && values.indexOf(value) === -1) values.push(value);
    });
    if (values.length >= 2) {
      groups.push({ option: names[d - 1] || ('Option ' + d), value: values, source: d });
    }
  }
  return groups;
}

// Mapping des variantes du compte EPROLO (fiche add_product, identifiants
// définitifs exigés par add_order) : l'envoi de commande résout le variantId
// à partir des valeurs cochées par le client (VariantsSelected du panier).
function eproloVariantMap(product) {
  return sortByPosition(product && product.variantlist).map(function (v) {
    return {
      variantId: String(v.id || ''),
      option1: v.option1 != null ? String(v.option1) : '',
      option2: v.option2 != null ? String(v.option2) : '',
      option3: v.option3 != null ? String(v.option3) : '',
      title: v.title != null ? String(v.title) : '',
      sku: v.sku != null ? String(v.sku) : '',
      position: toNumber(v.position) || 0,
      cost: toNumber(v.cost),
      stock: v.inventory_quantity !== undefined ? toNumber(v.inventory_quantity) : undefined
    };
  }).filter(function (v) { return v.variantId; });
}

// TablePriceVariants (même forme que ProductModalEdit.generateVariants) :
// une ligne par variante réelle avec les valeurs des groupes retenus, au prix
// de vente du produit (celui de la variante par défaut). La variante par
// défaut du compte EPROLO est marquée « Default » comme dans la fiche
// produit (la plus basse position : prix, stock et sku du produit).
function eproloTablePriceVariants(groups, variantMap, price, compare, defaultVariantId) {
  var result = [];
  variantMap.forEach(function (v) {
    var options = [];
    var values = [];
    var complete = true;
    groups.forEach(function (g) {
      var value = String(v['option' + g.source] || '').trim();
      options.push(g.option);
      values.push(value);
      if (!value) complete = false;
    });
    if (!options.length || !complete) return;
    result.push({
      option: options.length === 1 ? options[0] : options,
      value: values.length === 1 ? values[0] : values,
      price: price,
      compare_at_price: compare,
      default: String(v.variantId) === String(defaultVariantId) ? 'true' : 'false'
    });
  });
  return result;
}

export default {
  data() {
    return {
      loadingStore: true,
      storeDoc: null,
      host: '',
      catalogLoaded: false,
      rows: [],
      types: [],
      page: 1,
      typeId: '',
      subTypes: [],
      subTypeId: '',
      loadingSubs: false,
      productId: '',
      loadedFilterLabel: '',
      loadedPagesLabel: '',
      targetCategory: '',
      search: '',
      margin: DEFAULT_MARGIN,
      shippingEU: 0,
      overwrite: false,
      loadingPage: false,
      loadingAll: false,
      loadAllPage: 0,
      importing: false,
      importIndex: 0,
      importTotal: 0,
      importLabel: '',
      importReport: null,
      message: '',
      existingIds: null
    };
  },
  computed: {
    configured() {
      return !!(this.storeDoc && this.storeDoc.eproloEnabled && this.storeDoc.eproloApiKey && this.storeDoc.eproloApiSecret);
    },
    symbol() {
      return (this.storeDoc && this.storeDoc.symbol) || '€';
    },
    // Catégories existantes de la boutique (créées en majuscules par la page
    // Categories) : alimentent le menu « Target category » de l'import.
    storeCategories() {
      var list = (this.storeDoc && Array.isArray(this.storeDoc.categories)) ? this.storeDoc.categories : [];
      return list.filter(function (entry) { return entry && entry.category; });
    },
    filteredRows() {
      var query = this.search.trim().toLowerCase();
      if (!query) return this.rows;
      return this.rows.filter(function (row) {
        return String(row.name || '').toLowerCase().indexOf(query) !== -1 ||
          String(row.id || '').toLowerCase().indexOf(query) !== -1;
      });
    },
    selectedCount() {
      return this.rows.filter(function (row) { return row._selected; }).length;
    }
  },
  async mounted() {
    try {
      const data = await this.getStoresData();
      if (!data.length) {
        this.$router.push('/dashboard/default');
        return;
      }
      this.storeDoc = data[0];
      this.host = data[0].host;
      var margin = Number(this.storeDoc.eproloMargin);
      if (isFinite(margin) && this.storeDoc.eproloMargin !== '' && this.storeDoc.eproloMargin >= 0) {
        this.margin = margin;
      }
      var shippingEU = Number(this.storeDoc.eproloShippingEU);
      if (isFinite(shippingEU) && this.storeDoc.eproloShippingEU !== '' && shippingEU >= 0) {
        this.shippingEU = shippingEU;
      }
      if (this.configured) {
        // Catégories racine chargées d'avance : le filtre par catégorie est
        // prêt avant le premier chargement (échec non bloquant).
        await this.loadTypes();
        try {
          await this.loadExistingIds();
        } catch (error) {
          // The store may not have any Products collection yet: not fatal.
        }
      }
    } finally {
      this.loadingStore = false;
    }
  },
  methods: {
    sleep(ms) {
      return new Promise(function (resolve) { setTimeout(resolve, ms); });
    },
    // Appel à l'API Hermes (proxy serveur) : la clé et le secret EPROLO ne
    // quittent jamais le serveur, aucun souci de CORS ni de réseau navigateur.
    async eproloGet(resource, params) {
      var url = apiUrl + '/api/stores/' + encodeURIComponent(this.host) + '/eprolo/catalog/' + resource;
      return await axios.get(url, {
        params: params || {},
        headers: uploadHeaders(),
        timeout: 30000
      });
    },
    eproloErrorMessage(error) {
      // Les erreurs EPROLO arrivent dans error.response.data.error (502/504
      // avec le code applicatif EPROLO dans le texte du message).
      var detail = error && error.response && error.response.data && error.response.data.error
        ? String(error.response.data.error) : '';
      if (error.response) {
        var status = error.response.status;
        if (status === 401) return detail || "Hermes API key invalid or missing (x-api-key). Check config.json.";
        if (status === 409) return detail || "EPROLO is disabled or the key/secret is missing. Save the EPROLO settings first.";
        if (status === 504) return detail || "EPROLO did not answer in time. Retry later.";
        if (status === 502) return detail || "EPROLO returned an error.";
        if (status === 404) return detail || "Unknown catalog resource.";
        if (status === 500) return detail || "Hermes API server error.";
        return detail || "Hermes API returned HTTP " + status + ".";
      }
      return "Unable to reach the Hermes API (" + (error && error.message ? error.message : "network error") + ").";
    },
    payloadItems(data) {
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.list)) return data.list;
      if (data && Array.isArray(data.items)) return data.items;
      if (data && Array.isArray(data.data)) return data.data;
      return [];
    },
    // Catégories racine EPROLO : alimente le menu du filtre et les noms de
    // catégorie affichés dans le tableau (échec non bloquant).
    async loadTypes() {
      if (this.types.length) return;
      try {
        var res = await this.eproloGet('types');
        this.types = this.payloadItems(res.data);
      } catch (error) { /* menu déroulant vide : le catalogue reste utilisable */ }
    },
    // Sous-catégories de la catégorie choisie : types-two?typeid=... renvoie
    // un tableau direct [{id, name, waretypeid, typename, ...}].
    async loadSubTypes() {
      this.subTypes = [];
      this.subTypeId = '';
      if (!this.typeId) return;
      this.loadingSubs = true;
      try {
        var res = await this.eproloGet('types-two', { typeid: this.typeId });
        this.subTypes = this.payloadItems(res.data);
      } catch (error) {
        this.subTypes = [];
      } finally {
        this.loadingSubs = false;
      }
    },
    onCategoryChange() {
      // Nouvelle catégorie : la sous-catégorie précédente ne s'applique plus.
      this.page = 1;
      this.loadSubTypes();
    },
    resetFilters() {
      this.typeId = '';
      this.subTypeId = '';
      this.subTypes = [];
      this.productId = '';
      this.page = 1;
      this.search = '';
      // Les filtres s'appliquent au prochain « Load catalog ».
    },
    // Paramètres du catalogue EPROLO (page + filtres serveur vérifiés en
    // direct : wareTypeId = catégorie racine, wareTypeTwoId = sous-catégorie,
    // id = un seul produit ; page_size = produits par page, honoré jusqu'à
    // 200) et libellé du filtre actif — partagés par « Load catalog » et
    // « Load all pages ».
    buildCatalogParams(pageNumber) {
      var self = this;
      var params = { page: pageNumber, page_size: String(EPROLO_PAGE_SIZE) };
      var productId = String(this.productId || '').trim();
      var filterParts = [];
      if (productId) {
        params = { page: 1, id: productId };
        filterParts.push('id ' + productId);
      } else if (this.subTypeId) {
        params.wareTypeTwoId = this.subTypeId;
        var subMatch = this.subTypes.filter(function (s) { return String(s.id) === String(self.subTypeId); })[0];
        filterParts.push(subMatch && subMatch.name ? String(subMatch.name) : ('sub ' + this.subTypeId));
        var rootMatch = this.types.filter(function (t) { return String(t.id) === String(self.typeId); })[0];
        if (rootMatch && rootMatch.name) filterParts.push(String(rootMatch.name));
      } else if (this.typeId) {
        params.wareTypeId = this.typeId;
        var rootOnly = this.types.filter(function (t) { return String(t.id) === String(self.typeId); })[0];
        filterParts.push(rootOnly && rootOnly.name ? String(rootOnly.name) : ('type ' + this.typeId));
      }
      return { params: params, filterParts: filterParts };
    },
    // Fin naturelle du catalogue : au-delà de la dernière page, EPROLO répond
    // « code -1 : No data » (502 du proxy) — cas rencontré quand le nombre de
    // produits est un multiple exact de la taille de page.
    isCatalogEnd(error) {
      var detail = error && error.response && error.response.data && error.response.data.error
        ? String(error.response.data.error) : '';
      return !!(error && error.response && error.response.status === 502 && detail.indexOf('code -1') !== -1);
    },
    stopLoadAll() {
      this.loadingAll = false;
    },
    // « Load all pages » : suit la pagination EPROLO (100 produits par page)
    // jusqu'à la fin de la sélection courante (catégorie / sous-catégorie) et
    // remplace le contenu du tableau. Arrêt possible sur « Stop loading »,
    // garde-fou à 500 pages pour ne jamais boucler indéfiniment.
    async loadAllPages() {
      if (!this.configured || this.loadingPage || this.loadingAll || this.importing) return;
      if (String(this.productId || '').trim()) {
        // Un seul produit demandé (filtre id) : une page suffit.
        await this.loadPage();
        return;
      }
      this.loadingAll = true;
      this.loadAllPage = 0;
      this.importReport = null;
      this.catalogLoaded = false;
      this.loadedPagesLabel = 'Loading all pages';
      this.rows = [];
      var finished = false;
      var limitReached = false;
      try {
        await this.loadTypes();
        var built = this.buildCatalogParams(1);
        this.loadedFilterLabel = built.filterParts.join(' / ');
        var self = this;
        var seen = {};
        var pageNumber = 1;
        // Taille réelle d'une page pleine renvoyée par EPROLO : détectée sur
        // la première page, pour ne jamais conclure « fin » à tort si le
        // serveur plafonne ou ignore page_size.
        var effectivePageSize = 0;
        while (this.loadingAll) {
          if (pageNumber > LOAD_ALL_MAX_PAGES) {
            limitReached = true;
            break;
          }
          this.loadAllPage = pageNumber;
          var res;
          try {
            res = await this.eproloGet('products', Object.assign({}, built.params, { page: pageNumber }));
          } catch (error) {
            if (this.isCatalogEnd(error)) {
              finished = true;
              break;
            }
            if (!this.rows.length) {
              this.catalogLoaded = false;
              this.message = this.eproloErrorMessage(error);
              this.$bvModal.show('EproloAlert');
              return;
            }
            this.message = "Loading stopped at page " + pageNumber + ": " + this.eproloErrorMessage(error);
            this.$bvModal.show('EproloAlert');
            break;
          }
          var items = this.payloadItems(res.data);
          items.forEach(function (item) {
            var row = self.normalizeRow(item);
            if (!row.id || seen[row.id]) return;
            seen[row.id] = true;
            if (self.existingIds) row._exists = !!self.existingIds[self.rowDocId(row.id)];
            self.rows.push(row);
          });
          this.catalogLoaded = true;
          if (!items.length) {
            finished = true;
            break;
          }
          if (!effectivePageSize) effectivePageSize = items.length;
          else if (items.length < effectivePageSize) {
            finished = true;
            break;
          }
          pageNumber++;
          if (this.loadingAll) await this.sleep(LOAD_ALL_INTERVAL);
        }
      } finally {
        this.loadingAll = false;
      }
      if (finished) this.loadedPagesLabel = 'All pages';
      else if (limitReached) this.loadedPagesLabel = 'Pages 1-' + LOAD_ALL_MAX_PAGES + ' (limit reached)';
      else this.loadedPagesLabel = 'Pages 1-' + this.loadAllPage + ' (stopped)';
    },
    async loadPage() {
      if (!this.configured || this.loadingPage || this.loadingAll || this.importing) return;
      this.loadingPage = true;
      this.importReport = null;
      try {
        // Catégories racine (noms lisibles + menu du filtre) : non bloquant.
        await this.loadTypes();
        var self = this;
        // Filtres appliqués par EPROLO avant le chargement (vérifiés en
        // direct sur l'API) : wareTypeId = catégorie racine, wareTypeTwoId =
        // sous-catégorie, id = un seul produit. EPROLO n'offre pas de
        // recherche par mot-clé : le champ « Search » filtre la page chargée.
        var built = this.buildCatalogParams(this.page);
        this.loadedFilterLabel = built.filterParts.join(' / ');
        var res;
        try {
          res = await this.eproloGet('products', built.params);
        } catch (error) {
          this.rows = [];
          this.catalogLoaded = false;
          this.message = this.eproloErrorMessage(error);
          this.$bvModal.show('EproloAlert');
          return;
        }
        var items = this.payloadItems(res.data);
        this.rows = items.map(function (item) { return self.normalizeRow(item); });
        if (this.existingIds) {
          this.rows.forEach(function (row) {
            row._exists = !!self.existingIds[self.rowDocId(row.id)];
          });
        }
        this.catalogLoaded = true;
        this.loadedPagesLabel = 'Page ' + this.page;
      } finally {
        this.loadingPage = false;
      }
    },
    // Ligne à plat depuis un produit du catalogue public EPROLO : le coût et
    // le stock viennent de la variante par défaut (première position), les
    // images de imagelist (repli sur imagefirst).
    normalizeRow(product) {
      var p = product && typeof product === 'object' ? product : {};
      var variants = Array.isArray(p.variantlist) ? p.variantlist : [];
      var variant = null;
      if (variants.length) {
        var sorted = variants.slice().sort(function (a, b) {
          return (toNumber(a.position) || 0) - (toNumber(b.position) || 0);
        });
        variant = sorted[0];
      }
      var images = Array.isArray(p.imagelist) ? p.imagelist : [];
      var urls = images.map(function (img) { return img && img.src ? String(img.src) : ''; }).filter(Boolean);
      if (!urls.length && p.imagefirst) urls = [String(p.imagefirst)];
      return {
        id: String(p.id || ''),
        name: String(p.title || p.handle || p.id || ''),
        cost: toNumber(variant ? variant.cost : undefined),
        stock: variant && variant.inventory_quantity !== undefined ? toNumber(variant.inventory_quantity) : undefined,
        variantSku: variant ? String(variant.sku || '') : '',
        variantTitle: variant ? String(variant.title || '') : '',
        variants: variants.length,
        bodyHtml: String(p.body_html || ''),
        imageUrls: urls,
        typeId: firstTypeId(p),
        categoryName: this.typeName(firstTypeId(p)),
        _selected: false,
        _status: '',
        _message: '',
        _exists: false
      };
    },
    typeName(typeId) {
      var id = String(typeId || '');
      if (!id) return '';
      var match = this.types.filter(function (t) { return t && String(t.id) === id; })[0];
      return match && match.name ? String(match.name) : '';
    },
    rowDocId(productId) {
      return sanitizeDocId('ep-' + productId);
    },
    // Store category (upper case, as in the admin) : catégorie cible choisie
    // pour l'import, sinon nom de la catégorie EPROLO, sinon IMPORTED.
    rowCategoryName(row) {
      var name = String(this.targetCategory || row.categoryName || '').replace(/\s+/g, ' ').trim();
      if (!name) name = 'IMPORTED';
      if (name.length > 60) name = name.slice(0, 60);
      return name.toUpperCase();
    },
    storePrice(row) {
      if (row.cost == null) return "—";
      return round2((row.cost + this.shippingEU) * (1 + (Number(this.margin) || 0) / 100)).toFixed(2) + ' ' + this.symbol;
    },
    selectAll() {
      this.filteredRows.forEach(function (row) { row._selected = true; });
    },
    clearSelection() {
      this.rows.forEach(function (row) { row._selected = false; });
    },
    async loadExistingIds() {
      var snapshot = await firebase.firestore().collection('Sites').doc(this.host)
        .collection('Products').get();
      var map = {};
      snapshot.forEach(function (doc) { map[doc.id] = true; });
      this.existingIds = map;
      var self = this;
      this.rows.forEach(function (row) {
        row._exists = !!map[self.rowDocId(row.id)];
      });
    },
    async startImport() {
      if (this.loadingAll || this.loadingPage) return;
      var selected = this.rows.filter(function (row) { return row._selected; });
      if (!selected.length) return;
      this.importing = true;
      this.importReport = null;
      this.importIndex = 0;
      this.importTotal = selected.length;
      if (!this.existingIds) {
        try {
          await this.loadExistingIds();
        } catch (error) {
          // No Products collection yet.
        }
      }
      if (!this.existingIds) this.existingIds = {};
      var created = 0;
      var skipped = 0;
      var errors = 0;
      var missingCategories = [];

      for (var k = 0; k < selected.length; k++) {
        var row = selected[k];
        this.importIndex = k + 1;
        this.importLabel = "Importing " + (row.name || row.id);
        row._status = 'working';
        row._message = '';
        try {
          var docId = this.rowDocId(row.id);
          if (!row.id) throw new Error("Missing EPROLO id, cannot import.");
          if (this.existingIds[docId] && !this.overwrite) {
            row._status = 'skipped';
            row._message = "Product already exists in the store.";
            skipped++;
            continue;
          }
          // 1. Le produit est d'abord ajouté au compte EPROLO (add_product) :
          // EPROLO lui attribue des identifiants définitifs (produit et
          // variantes) exigés ensuite par add_order.
          var importRes = await axios.post(
            apiUrl + '/api/stores/' + encodeURIComponent(this.host) + '/eprolo/import',
            { ids: [row.id] },
            { headers: uploadHeaders(), timeout: 60000 }
          );
          var items = (importRes.data && Array.isArray(importRes.data.items)) ? importRes.data.items : [];
          if (!items.length) throw new Error("EPROLO did not return the imported product.");
          var imported = items[0];
          // 2. Écriture dans la boutique avec les identifiants du compte.
          var product = this.buildProductDoc(row, docId, imported);
          await firebase.firestore().collection('Sites').doc(this.host)
            .collection('Products').doc(docId).set(product);
          this.existingIds[docId] = true;
          row._status = 'done';
          row._exists = true;
          created++;
          if (missingCategories.indexOf(product.category) === -1) {
            missingCategories.push(product.category);
          }
          await this.sleep(IMPORT_INTERVAL);
        } catch (error) {
          row._status = 'error';
          row._message = error && error.response && error.response.data && error.response.data.error
            ? error.response.data.error : (error && error.message ? error.message : String(error));
          errors++;
        }
      }

      await this.addMissingCategories(missingCategories);
      this.importReport = { created: created, skipped: skipped, errors: errors };
      this.importing = false;
      this.importLabel = '';
    },
    // Adds the categories of imported products that do not exist yet in the
    // store (same object shape as the Categories page of the admin).
    async addMissingCategories(categories) {
      var current = Array.isArray(this.storeDoc.categories) ? this.storeDoc.categories.slice() : [];
      var added = false;
      categories.forEach(function (name) {
        if (!name) return;
        var exists = current.some(function (entry) { return entry && entry.category === name; });
        if (!exists) {
          current.push({
            category: name,
            description: '',
            image: '',
            offer: '',
            metaTitle: '',
            metaDescription: '',
            metaKeywords: ''
          });
          added = true;
        }
      });
      if (!added) return;
      try {
        await firebase.firestore().collection('Sites').doc(this.host).update({ categories: current });
        this.storeDoc.categories = current;
      } catch (error) {
        this.message = "Products imported, but adding the new categories failed: " + error;
        this.$bvModal.show('EproloAlert');
      }
    },
    stripHtml(value) {
      return String(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 300);
    },
    fileNameFromUrl(url) {
      try {
        return decodeURIComponent(url.split('/').pop().split('?')[0]) || 'image';
      } catch (error) {
        return 'image';
      }
    },
    // Product document with the exact same fields as the "Add product"
    // window of the admin, so the storefront (ecom) renders it normally.
    // « imported » est la fiche renvoyée par add_product : elle porte les
    // identifiants définitifs du compte EPROLO (bloc eprolo).
    buildProductDoc(row, docId, imported) {
      var marginFactor = 1 + (Number(this.margin) || 0) / 100;
      var shippingEU = Number(this.shippingEU) || 0;
      var cost = row.cost != null ? row.cost : 0;
      // Frais de port EU inclus dans le prix de vente (réglage EPROLO de
      // l'admin) : le menu Shipping peut rester sur « Free shipping ».
      // Prix arrondi au x9.99 supérieur, et « compare at » = x9.99 supérieur
      // à prix / 0.8 : réduction affichée d'au moins 20 % (ex. 23,47 calculé
      // → 29,99 vendu, 39,99 barré).
      var withShipping = cost + shippingEU;
      var price = charmPrice(withShipping * marginFactor);
      var compareAt = charmPrice(price / 0.8);
      var name = row.name || docId;
      var urls = row.imageUrls.slice(0, 10);
      var images = urls.map(function (url, index) {
        return {
          image_id: 'ep-' + (row.id || docId) + '-' + index,
          src: url,
          alt: name,
          variant_id: [],
          filename: null
        };
      });
      var self = this;
      images.forEach(function (image) { image.filename = self.fileNameFromUrl(image.src); });
      var firstImage = images.length ? images[0].src : '';
      var category = this.rowCategoryName(row);
      // Variante par défaut du compte EPROLO (position la plus basse) : prix
      // de vente, stock et variantId de repli quand la commande ne précise
      // pas de variante. Les autres variantes sont résolues à l'envoi de la
      // commande (champ VariantsSelected du panier).
      var sorted = sortByPosition(imported && imported.variantlist);
      var variant = sorted[0] || {};
      // Variantes importées : groupes affichés par le storefront (noms des
      // options depuis optionlist), combinaisons pour l'éditeur de l'admin et
      // mapping complet (variantId du compte) pour la résolution des commandes.
      // Enregistrées dans la forme exacte de la fiche produit (création
      // manuelle) : variants = [{ option, value }] uniquement et
      // TablePriceVariants comme « Generate » (la variante par défaut du
      // compte est marquée « Default »).
      var variantGroups = eproloVariantGroups(imported);
      var eproloVariants = eproloVariantMap(imported);
      var productVariants = variantGroups.map(function (g) {
        return { option: g.option, value: g.value };
      });
      var tablePriceVariants = eproloTablePriceVariants(variantGroups, eproloVariants, price, compareAt, String(variant.id || ''));
      return {
        id: docId,
        title: name,
        slug: slug(name, '-'),
        description: row.bodyHtml || '',
        price: price,
        compare_at_price: compareAt,
        callToAction: 'Add to cart',
        cost_price: round2(cost),
        images: images,
        image1: firstImage,
        image2: images[1] ? images[1].src : '',
        image3: images[2] ? images[2].src : '',
        image4: images[3] ? images[3].src : '',
        // Aucune section « article » créée par défaut : la vitrine n'affiche
        // que des sections remplies, et la fiche admin peut en ajouter
        // (bouton Add section) si besoin.
        article: [],
        status: true,
        backgroundColor: '#ffffff',
        ReviewBackgroundColor: '#ffffff',
        ReviewTextColor: '#000000',
        // Le formulaire « Add a Review » de la vitrine ne s'affiche que si
        // cette case est vraie (même interrupteur que la fiche produit).
        addingreviews: true,
        textColor: '#000000',
        opacity: '0.5',
        vendors: [],
        contact: {
          title: '',
          firstname: false,
          lastname: true,
          address: false,
          city: true,
          postcode: false,
          country: false,
          phone: false,
          Textcolor: '#000000',
          BackgroundColor: '#ffffff',
          opacity: '0.5'
        },
        sku: String(variant.sku || row.variantSku || docId),
        stock: variant.inventory_quantity !== undefined && toNumber(variant.inventory_quantity) !== undefined
          ? toNumber(variant.inventory_quantity) : DEFAULT_STOCK,
        weight: variant.weight !== undefined ? String(variant.weight) : '',
        category: category,
        metaTitle: name,
        metaKeywords: '',
        metaDescription: this.stripHtml(row.bodyHtml),
        variants: productVariants,
        TablePriceVariants: tablePriceVariants,
        reviews: [],
        FirstImage: firstImage,
        affiliateLink: '',
        affiliate: false,
        landing: false,
        ShowRating: true,
        ShowSimilarProduct: true,
        ShowIndicator: true,
        ShowSubscriber: true,
        eprolo: {
          provider: 'eprolo',
          productId: String(imported.id || row.id),
          variantId: String(variant.id || ''),
          variantTitle: String(variant.title || ''),
          variants: eproloVariants,
          sourceId: String(row.id || ''),
          margin: Number(this.margin) || 0,
          shippingEU: shippingEU,
          importedAt: new Date().toISOString()
        }
      };
    },
    async getStoresData() {
      let listData = [];
      var ProductRef = await firebase.firestore().collection("Sites")
        .where("host", "==", this.getStore())
        .get()
        .then((querySnapshot) => {
          querySnapshot.forEach((doc) => {
            listData.push(
              doc.data()
            );
          });
        });
      return listData;

    },
    getStore() {
      const host = Cookies.get('hostCookie');
      if (host) {
        return host;
      } else {
        this.$router.push('/dashboard/default');
      }
    }
  }
};
</script>

<style lang="scss" scoped>
table.text-center * {
  text-align: center;
}

.eprolo-thumb {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 4px;
  display: block;
}
</style>
